import React, { useState } from "react";
import { BookOpen, Clock, IndianRupee, Monitor, PlayCircle } from "lucide-react";
import { AppCard, AppCardContent, StatusBadge } from "../../ui";

/** One fact in the meta line: a glyph and a short phrase. */
const Fact = ({ icon: Icon, children }) => (
  <li className="tw-inline-flex tw-min-w-0 tw-items-center tw-gap-1.5">
    <Icon size={14} className="tw-shrink-0 tw-text-content-subtle" aria-hidden="true" />
    <span className="tw-truncate">{children}</span>
  </li>
);

/**
 * The course's picture: its catalogue thumbnail, or a book on the brand tint
 * when it has none or the image fails to load. Decorative — the name beside
 * it says which course this is.
 */
const CourseMark = ({ src }) => {
  const [failed, setFailed] = useState(false);
  if (src && !failed) {
    return (
      <img
        src={src}
        alt=""
        onError={() => setFailed(true)}
        className="tw-h-14 tw-w-14 tw-shrink-0 tw-rounded tw-border tw-border-line tw-object-cover"
      />
    );
  }
  return (
    <span className="tw-flex tw-h-14 tw-w-14 tw-shrink-0 tw-items-center tw-justify-center tw-rounded tw-bg-primary/10 tw-text-primary">
      <BookOpen size={26} aria-hidden="true" />
    </span>
  );
};

/**
 * CourseIdentityCard — which course this is, its state, and the page's actions.
 *
 * It decides nothing about permissions: the page builds `actions` from what the
 * caller may do and passes them in.
 *
 * Props:
 *   course        — the `GET /courses/:id` course
 *   fee           — courseDetails.courseFee()
 *   websiteListed — true / false, or null when the institute has no website
 *   actions       — the buttons on the right
 */
const CourseIdentityCard = ({ course, fee, websiteListed = null, actions }) => {
  const freeDemo = String(course.freeDemo || "").toLowerCase() === "yes";

  return (
    <AppCard as="section" aria-label="Course summary">
      <AppCardContent className="tw-flex tw-flex-col tw-gap-4 min-[1200px]:tw-flex-row min-[1200px]:tw-items-start">
        <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-start tw-gap-4">
          <CourseMark src={course.thumbnailUrl} />

          <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-1.5">
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
              <h2 className="tw-m-0 tw-min-w-0 tw-break-words tw-text-[1.375rem] tw-font-semibold tw-leading-tight tw-tracking-tight tw-text-content">
                {course.name}
              </h2>
              {course.shortCode && (
                <span className="tw-rounded-[6px] tw-border tw-border-line tw-bg-surface-sunken tw-px-1.5 tw-py-0.5 tw-font-mono tw-text-xs tw-text-content-muted">
                  <span className="tw-sr-only">Short code </span>
                  {course.shortCode}
                </span>
              )}
              {websiteListed === true && <StatusBadge status="success">On your website</StatusBadge>}
              {websiteListed === false && <StatusBadge status="neutral">Hidden from website</StatusBadge>}
            </div>

            {course.title && (
              <p className="tw-m-0 tw-break-words tw-text-sm tw-text-content-muted">{course.title}</p>
            )}

            <ul className="tw-m-0 tw-mt-1 tw-flex tw-list-none tw-flex-wrap tw-gap-x-4 tw-gap-y-1 tw-p-0 tw-text-[0.8125rem] tw-text-content-muted">
              {fee.text && <Fact icon={IndianRupee}>{fee.text}</Fact>}
              {course.duration && <Fact icon={Clock}>{course.duration}</Fact>}
              {course.mode && <Fact icon={Monitor}>{course.mode}</Fact>}
              {freeDemo && <Fact icon={PlayCircle}>Free demo class</Fact>}
            </ul>
          </div>
        </div>

        {actions && (
          <div className="tw-flex tw-shrink-0 tw-flex-wrap tw-items-center tw-gap-2">
            {actions}
          </div>
        )}
      </AppCardContent>
    </AppCard>
  );
};

export default CourseIdentityCard;
